import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import goTo from 'vuetify/lib/services/goto'
import finAPI from "@/js/finAPI"
import finBrowser from "@/js/finBrowser"
import {compare} from 'compare-versions'

Vue.use(Router)

// A few tips first:

// next(): move on to the next hook in the pipeline. If no hooks are left, the navigation is confirmed

// next('/') or next({ path: '/' }): edirect to a different location. The current navigation
// will be aborted and a new one will be started. You can pass any location object to next,
// which allows you to specify options like replace: true, name: 'home' and any option used in
// router-link's to prop or router.push

// beforeEnter guards only trigger when entering the route, they don't trigger when the params, query or hash change
// e.g. going from /users/2 to /users/3 or going from /users/2#info to /users/2#projects. They are only triggered
// when navigating from a different route.


const doNavigationControl = function (state, from, to, next) {

  // are there open invoices with due date in the past?
  let hasOpenInvoices = false
  if (state.profile && state.profile.paymentDeadline) {
    const today = new Date()
    const todayMs = new Date(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`).getTime()
    const openInvoicesDueDateMS = new Date(state.profile.paymentDeadline).getTime()
    const openInvoicesDueDatePast = todayMs > openInvoicesDueDateMS
    hasOpenInvoices = state.profile.paymentStatus === 'open' && openInvoicesDueDatePast
  }

  const isAppleUser = state.user && state.user.username.startsWith("SignInWithApple_")
  // the user must have verified his email address and must have finished onboarding before using the protected pages
  const emailVerified = state.authorized.email_verified || isAppleUser
  if (state.authorized && emailVerified && !hasOpenInvoices && !store.getters['auth/showOnBoarding']) {
    const isRedirection = to.name !== 'protected' && to.path !== '/protected/'
    console.log("Calling next ", isRedirection ? true : '/protected/myFinHub')
    next(isRedirection ? true : '/protected/myFinHub')
  }
  // the user did not verify his email address yet
  else if (state.authorized && !emailVerified) {
    if (to.name !== 'finVerifyEmail') {
      console.log("Calling next protected/verify-email")
      next('/protected/verify-email')
    } else {
      console.log("Calling next() - state.authorized && !emailVerified")
      next()
    }
  } else if (state.authorized && ['dataProtection', 'dataProtectionProtected', 'demotool'].includes(to.name)) {
    console.log("state.authorized && ['dataProtection', 'demotool'].includes(to.name)")
    next()
  }
  // the user has opened invoices he needs to pay before being allowed to navigate to other protected pages
  else if (hasOpenInvoices) {
    console.log('User must pay his open invoices, due in the past.', to, state, hasOpenInvoices)
    if (to.name !== 'myProfile' || (to.name === 'myProfile' && to.params.tab !== 'payments')) {
      console.log("Calling next /protected/myProfile/payments")
      next({
        name: 'myProfile',
        query: {
          tab: 'payments',
        }
      })
    } else {
      console.log("Calling next() - hasOpenInvoices)")
      next()
    }
  }
  // the user needs to go to the onboarding or fasttrack
  else if (state.authorized && store.getters['auth/showOnBoarding'] && to.name !== 'onbregstart') {
    if (to.name !== 'fasttrack' && (from.name === null || from.name === 'root' || from.path === '/verify-email' || from.path === '/regulations')) {
      console.log(`Call onboarding process after (social media) login - Calling next /onboarding/fasttrack`, from, to)
      next('/onboarding/fasttrack')
    } else {
      next()
    }
  } else {
    if (state.authorized) {
      next('/')
    } else {
      next()
    }
  }
}

const protectedRoutes = {
  path: '/protected',
  name: 'protected',
  meta: {
    breadcrumbIgnore: true
  },
  component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/ProtectedApp.vue'),
  beforeEnter(to, from, next) {
    store.dispatch('auth/init').then((state) => {
      if (state.authorized) {
        console.log("protectedRoutes beforeEnter to", to.fullPath)
        doNavigationControl(state, from, to, next)
      } else {
        console.log("protectedRoutes beforeEnter to NOT authorized - next to / with redirect param", to.fullPath)
        next({path: '/', query: {redirect: to.fullPath}})
      }
    }).catch((e) => {
      console.error("protectedRoutes beforeEnter error - next to /", to.fullPath, e)
      next({
        path: '/',
        query: {
          redirect: to.fullPath,
        }
      })
    })
  },
  children: [
    {
      path: 'verify-email',
      name: 'finVerifyEmail',
      meta: {newPage: true, breadcrumbName: 'E-Mail verifizieren'},
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finVerifyEmail.vue'),
    },
    {
      path: 'finSpeaker/:msgId?',
      name: 'finSpeaker',
      props: true,
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finSpeaker.vue'),
    },
    {
      path: 'maintenance',
      name: 'maintenance',
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/maintenance.vue'),
    },
    {
      path: 'myFinHub',
      name: 'myFinHub',
      meta: {
        breadcrumbName: 'Dashboard'
      },
      component: () => import(/* webpackChunkName: "MyFinHub" */ '../views/protected/myFinHub.vue'),
      children: [
        {
          path: 'portfolio/:id/:tab?',
          name: 'portfolioDetail',
          props: true,
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finPortfolio/PortfolioDetail.vue'),
          children: [
            {
              path: 'addInstrumentsToPortfolio/:assetClassId',
              name: 'addInstrumentsToPortfolio',
              // props has to be set to true in order to be able to retrieve the portfolio id in this page
              props: true,
              meta: {newPage: true, breadcrumbName: 'Wertpapier hinzufügen', action: 'portfolio'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/common/DataTable/AddInstruments/AddInstruments.vue')
            },
            {
              path: 'createOrderList',
              name: 'createOrderList',
              props: true,
              meta: {newPage: true, breadcrumbName: 'Orderliste erstellen'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '@/views/protected/finPortfolio/CreateOrderList.vue')
            }
          ]
        },
        {
          path: 'company/:id',
          name: 'companyDetail',
          props: true,
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finCompanyPage/index.vue'),
          children: [
            {
              path: 'addCompanyPageInstrumentToPortfolio/:action?/:portfolioId?',
              name: 'addCompanyPageInstrumentToPortfolio',
              props: true,
              meta: {newPage: true, breadcrumbName: 'Aktie hinzufügen', action: 'portfolio'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finCompanyPage/AddToPortfolio.vue')
            }
          ]
        },
        {
          path: 'etf/:id',
          name: 'etfDetail',
          props: true,
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finEtfPage/index.vue'),
          children: [
            {
              path: 'addEtfToPortfolio/:assetClassId/:action?/:portfolioId?',
              name: 'addEtfToPortfolio',
              props: true,
              meta: {newPage: true, breadcrumbName: 'ETF hinzufügen', action: 'portfolio'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finEtfPage/AddEtfToPortfolio.vue')
            }
          ]
        }
      ]
    },
    {
      path: 'finNews',
      name: 'news',
      meta: {
        icon: 'icon-news',
        breadcrumbName: 'News'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finNews_Boerse.vue'),
      children: [
        {
          path: ':id',
          name: 'newsDetail',
          props: true,
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/NewsDetail.vue')
        }
      ]
    },
    {
      path: 'finTools',
      name: 'finTools',
      meta: {
        icon: 'icon-tools',
        breadcrumbName: 'Tools'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finTools.vue'),
      children: [
        {
          path: 'shareScreener',
          name: 'shareScreener',
          meta: {
            breadcrumbName: 'Aktien-Screener'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/ShareScreener.vue'),
          children: [
            {
              path: 'exec/:id/',
              name: 'shareScreenerExecute',
              props: true,
              meta: {
                breadcrumbName: 'Ergebnisse'
              },
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/screenerResultList/index.vue'),
              children: [
                {
                  path: 'addScreenerInstrumentsToPortfolio',
                  name: 'addScreenerInstrumentsToPortfolio',
                  // props has to be set to true in order to be able to retrieve the screener id in this page
                  props: true,
                  component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/screenerResultList/AddToPortfolio.vue')
                }
              ]
            },
            {
              path: ':id',
              name: 'shareScreenerEdit',
              meta: {
                breadcrumbName: 'Bearbeiten'
              },
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/sharesScreenerMask/index.vue')
            }
          ]
        },
        {
          path: 'etfScreener',
          name: 'etfScreener',
          meta: {
            breadcrumbName: 'ETF-Screener'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/ETFScreener.vue'),
          children: [
            {
              path: 'exec/:id',
              name: 'etfScreenerExecute',
              props: true,
              meta: {
                breadcrumbName: 'Ergebnisse'
              },
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/screenerResultList/index.vue'),
              children: [
                {
                  path: 'addEtfScreenerInstrumentsToPortfolio',
                  name: 'addEtfScreenerInstrumentsToPortfolio',
                  // props has to be set to true in order to be able to retrieve the screener id in this page
                  props: true,
                  component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/screenerResultList/AddToPortfolio.vue')
                }
              ]
            },
            {
              path: ':id',
              name: 'etfScreenerEdit',
              meta: {
                breadcrumbName: 'Bearbeiten'
              },
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finScreener/etfScreenerMask/index.vue')
            }
          ]
        },
        {
          path: 'rate/shares',
          name: 'finToolsRateShares',
          meta: {
            breadcrumbName: 'Aktie bewerten'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finTools/rate/shares/index.vue'),
          children: [
            {
              path: 'create-rating/:sid',
              name: 'finToolsRateSharesRatingCreate',
              meta: {
                breadcrumbName: 'Bewertung'
              },
              props: true,
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finTools/rate/shares/subpages/FinRateSharesRating.vue'),
              children: [
                {
                  path: 'addShareRateToPortfolio',
                  name: 'addShareRateToPortfolio',
                  // props has to be set to true in order to be able to retrieve the share id in this page
                  props: true,
                  component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/rate/shares/AddToPortfolio.vue')
                }
              ]
            },
            {
              path: 'exec-rating/:srId',
              name: 'finToolsRateSharesRatingExec',
              meta: {
                breadcrumbName: 'Bewertung'
              },
              props: true,
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finTools/rate/shares/subpages/FinRateSharesRating.vue'),
              children: [
                {
                  path: 'addShareRateExecToPortfolio',
                  name: 'addShareRateExecToPortfolio',
                  // props has to be set to true in order to be able to retrieve the share id in this page
                  props: true,
                  component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/rate/shares/AddToPortfolio.vue')
                }
              ]
            },
          ]
        },
        {
          path: 'etfAnalyzer',
          name: 'etfAnalyzer',
          meta: {
            breadcrumbName: 'ETF-Analyzer'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finAnalyzer/ETFAnalyzer.vue'),
        },
        {
          path: 'indexAnalyzer',
          name: 'indexAnalyzer',
          meta: {
            breadcrumbName: 'Index-Analyzer'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finAnalyzer/IndexAnalyzer.vue'),
        },
        {
          path: 'performanceanalyzer1',
          name: 'performanceanalyzer1',
          meta: {
            breadcrumbName: 'Performance-Analyzer'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finAnalyzer/PerformanceAnalyzer1.vue'),
        },
        {
          path: 'backtesting',
          name: 'backtesting',
          meta: {
            breadcrumbName: 'Backtesting'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finTools/finAnalyzer/Backtesting.vue'),
        },
        {
          path: 'sektorAnalyzer',
          name: 'sektorAnalyzer',
          meta: {
            breadcrumbName: 'Sektor-Analyzer'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '@/components/pages/finTools/finAnalyzer/SektorAnalyzer.vue'),
        },
        {
          path: 'vergleicheAktien',
          name: 'vergleicheAktien',
          meta: {
            breadcrumbName: 'Vergleiche Aktien'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '@/components/pages/finTools/finCompare/CompareShare.vue'),
        },
        {
          path: 'vergleicheEtfs',
          name: 'vergleicheEtfs',
          meta: {
            breadcrumbName: 'Vergleiche ETFs'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '@/components/pages/finTools/finCompare/CompareETF.vue'),
        },
      ]
    },
    {
      path: 'finAcademy',
      name: 'finAcademy',
      meta: {
        icon: 'icon-academy',
        breadcrumbName: 'Academy'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finAcademy.vue'),
      children: [
        {
          path: 'entry/:entryId',
          name: 'academyEntry',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Academy'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finAcademy/AcademyEntry.vue'),
        },
        {
          path: 'tag/:tagId',
          name: 'academyTag',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Academy'
          },
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finAcademy/AcademyCategory.vue'),
        }
      ]
    },
    {
      path: 'finStrategy',
      name: 'finStrategy',
      meta: {
        icon: 'icon-strategy',
        breadcrumbName: 'Strategy'
      },
      component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy.vue'),
      children: [
        {
          path: 'list/shares',
          name: 'finStrategy_list_shares',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Alle Strategien'
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyResult.vue'),
          children: [
            {
              path: ':stratId',
              name: 'finStrategy_details',
              props: true,
              meta: {newPage: true},
              component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyDetails.vue')
            },
          ]
        },
        {
          path: 'list/shares/:mode/:assetClassID/:portfolioId/:goThenToPage',
          name: 'finStrategy_list_shares_portfolio',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Alle Strategien'
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyResult.vue'),
          children: [
            {
              path: ':stratId',
              name: 'finStrategy_details_portfolio',
              props: true,
              meta: {newPage: true, breadcrumbName: 'Details'},
              component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyDetails.vue')
            },
          ]
        },
        {
          path: 'list/etfs',
          name: 'finStrategy_list_etfs',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Alle Strategien'
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyResult.vue'),
          children: [
            {
              path: ':stratId',
              name: 'finStrategy_etfdetails',
              props: true,
              meta: {newPage: true, breadcrumbName: 'Details'},
              component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyDetails.vue')
            },
          ]
        },
        {
          path: 'list/etfs/:mode/:assetClassID/:portfolioId/:goThenToPage',
          name: 'finStrategy_list_etfs_portfolio',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'Alle Strategien'
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyResult.vue'),
          children: [
            {
              path: ':stratId',
              name: 'finStrategy_etfdetails_portfolio',
              props: true,
              meta: {newPage: true, breadcrumbName: 'Details'},
              component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/protected/finStrategy/StrategyDetails.vue')
            },
          ]
        },
        {
          path: 'finProfiler/:origin?',
          name: 'finStrategy_profiler',
          props: true,
          meta: {
            newPage: true,
            breadcrumbName: 'finProfiler',
            fixedFooter: true
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/onboarding/index.vue')
        },
        {
          path: 'finStrategist',
          name: 'finStrategy_strategist',
          meta: {
            newPage: true,
            breadcrumbName: 'finStrategist',
            fixedFooter: true
          },
          component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/onboarding/index.vue')
        }
      ]
    },
    {
      path: 'myWatchlists',
      name: 'myWatchlists',
      meta: {
        breadcrumbName: 'Watchlists'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/MyWatchlists.vue'),
      children: [
        {
          path: 'watchlist/:id/:tab?',
          name: 'watchlistDetails',
          props: true,
          component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/myWatchlist/WatchlistDetails/index.vue'),
          children: [
            {
              path: 'addInstrumentsToWatchlist/:assetClassId',
              name: 'addInstrumentsToWatchlist',
              // props has to be set to true in order to be able to retrieve the watchlist id in this page
              props: true,
              meta: {newPage: true, isWatchlist: true, breadcrumbName: 'Wertpapier hinzufügen', action: 'watchlist'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/common/DataTable/AddInstruments/AddInstruments.vue'),
            },
            {
              path: 'notification/:itemId',
              name: 'WatchlistNotification',
              props: true,
              meta: {
                newPage: true,
                breadcrumbName: 'Limits einstellen'
              },
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/myWatchlist/WatchlistNotification/index.vue')
            },
            {
              path: 'addWatchlistsInstrumentsToPortfolio/:assetClassId/:instrumentId',
              name: 'addWatchlistsInstrumentsToPortfolio',
              // props has to be set to true in order to be able to retrieve the watchlist id in this page
              props: true,
              meta: {newPage: true, action: 'watchlist'},
              component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/myWatchlist/WatchlistDetails/AddToPortfolio.vue')
            }
          ]
        }
      ]
    },
    {
      path: 'myProfile/:tab?',
      name: 'myProfile',
      meta: {
        breadcrumbName: 'Mein Profil'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/MyProfile.vue')
    },
    {
      path: 'imprint',
      alias: 'finImprint',
      name: 'imprintProtected',
      meta: {
        breadcrumbName: 'Impressum'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/public/finImprint.vue')
    },
    {
      path: 'data-protection',
      alias: 'dataProtection',
      name: 'dataProtectionProtected',
      meta: {
        breadcrumbName: 'Datenschutzerklärung'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/public/finDataProtection.vue')
    },
    {
      path: 'risk-warnings',
      alias: 'riskWarnings',
      name: 'riskWarningsProtected',
      meta: {
        breadcrumbName: 'Risikohinweise finCraft'
      },
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finRiskWarnings.vue')
    },
    {
      path: 'agb',
      alias: 'finAgb',
      name: 'finAgb',
      component: () => import(/* webpackChunkName: "PublicApp" */ '../views/public/finAGB.vue')
    },
    {
      meta: {
        breadcrumbIgnore: true
      },
      path: 'pageNotFound',
      name: 'pageNotFoundProtected',
      component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/public/PageNotFound')
    },
    {
      path: '*',
      redirect: '/protected/pageNotFound',
      component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/public/PageNotFound')
    }
  ]
}

if (process.env.VUE_APP_HIDE_COMING_FEATURES === 'false') {
  let comingFeaturesRoute = {
    path: 'coming-features',
    name: 'comingFeatures',
    meta: {
      icon: 'icon-finbroker',
      breadcrumbName: 'Brokerage'
    },
    component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/comingFeatures.vue'),
  }
  protectedRoutes.children.unshift(comingFeaturesRoute)
}

if (process.env.VUE_APP_HIDE_COMMUNITY === 'false' || process.env.VUE_APP_DISABLE_COMMUNITY === 'false') {
  let communityRoute = {
    path: 'finCommunity',
    name: 'community',
    meta: {
      icon: 'icon-community',
      breadcrumbName: 'Community'
    },
    component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finCommunity.vue'),
    children: [
      {
        path: 'forum',
        name: 'forum',
        component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finCommunity/forum/Forum.vue'),
        children: [
          {
            path: 'add',
            name: 'forum_add',
            component: () => import(/* webpackChunkName: "ProtectedApp" */ '../components/pages/finCommunity/forum/ForumAdd.vue')
          }
        ]
      }
    ]
  }
  protectedRoutes.children.unshift(communityRoute)
}

const publicRoutes = {
  path: '/',
  name: 'public',
  component: () => import(/* webpackChunkName: "PublicApp" */ '../views/public/PublicApp.vue'),
  beforeEnter(to, from, next) {
    if (to.name !== "updateapp") {
      store.dispatch('auth/init').then((state) => {
        if (state.authorized && !['finRegulations', 'preview', 'demotool', 'dataProtection', 'abonnements'].includes(to.name) //TODO finRegulations in protected before each
        ) {
          console.log("publicRoutes beforeEnter myFinHub")
          next({name: 'myFinHub'})
        } else {
          console.log("publicRoutes beforeEnter", to.fullPath)
          next()
        }
      }).catch((e) => {
        console.error('publicRoutes beforeEnter, e', to.fullPath, e)
        next()
      })
    } else {
      console.log('publicRoutes beforeEnter else', to.fullPath)
      next()
    }
  },
  children: [
    {
      path: '/',
      name: 'root',
      component: () => import(/* webpackChunkName: "PublicApp" */ '../views/public/Login.vue')
    },
    {
      path: 'updateapp',
      name: 'updateapp',
      component: () => import(/* webpackChunkName: "Login" */ '@/views/public/UpdateApp.vue')
    },
    {
      path: 'tracking',
      name: 'trackinginfo',
      component: () => import(/* webpackChunkName: "Login" */ '@/views/public/TrackingInfo.vue')
    },
    {
      path: 'signin',
      name: 'signin',
      component: () => import(/* webpackChunkName: "PublicApp" */ '@/views/public/SigningIn.vue'),
    },
    {
      path: 'signout',
      name: 'signout',
      component: () => import(/* webpackChunkName: "PublicApp" */ '@/views/public/SigningOut.vue'),
    },
    {
      path: 'preview',
      name: 'preview',
      component: () => import(/* webpackChunkName: "PublicApp" */ '@/views/public/Preview.vue'),
    },
    // page to accept agbs, data protection and regulations documents (bafin documents)
    {
      path: 'regulations',
      name: 'finRegulations',
      meta: {newPage: true, breadcrumbName: 'Regulations'},
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/protected/finRegulations.vue')
    },
    {
      path: 'data-protection',
      alias: 'dataProtection',
      name: 'dataProtection',
      component: () => import(/* webpackChunkName: "ProtectedApp" */ '../views/public/finDataProtection.vue')
    },
    {
      path: 'demotool',
      name: 'demotool',
      component: () => import(/* webpackChunkName: "DemoTool" */ '../views/public/DemoTool.vue')
    },
    {
      path: 'abonnements',
      name: 'abonnements',
      component: () => import(/* webpackChunkName: "DemoTool" */ '../views/public/Abonnements.vue')
    },
    {
      path: 'pageNotFound',
      name: 'pageNotFound',
      component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/public/PageNotFound')
    },
    {
      path: '*',
      redirect: '/pageNotFound',
      component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/public/PageNotFound')
    }
  ]
}

const routes = [protectedRoutes]

if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'dev') {
  const developmentRoutes = {
    path: '/dev',
    component: () => import('../views/development/ComponentSamples.vue')
  }
  routes.push(developmentRoutes)
}

const registrationRoutes = {
  path: '/registration',
  component: () => import(/* webpackChunkName: "PublicApp" */ '../views/registration/index.vue'),
  beforeEnter(to, from, next) {
    store.dispatch('auth/init').then(() => {
      next()
    }).catch(() => next())
  },
  children: [
    {
      path: 'start',
      name: 'onbregstart',
      meta: {hideSlider: true},
      component: () => import(/* webpackChunkName: "PublicApp" */ '../components/pages/onboarding/Step1/Registration/index.vue')
    },
    {
      path: 'verify',
      name: 'onbverify',
      meta: {hideSlider: true},
      component: () => import(/* webpackChunkName: "PublicApp" */ '../components/pages/onboarding/Step1/Verification/index.vue')
    },
    {
      path: 'reset',
      name: 'onbreset',
      meta: {hideSlider: true},
      component: () => import(/* webpackChunkName: "PublicApp" */ '../components/pages/onboarding/Step1/Verification/index.vue')
    }
  ]
}

const newsletterRoutes = {
  path: '/newsletter',
  component: () => import(/* webpackChunkName: "Newsletter" */ '../views/newsletter/index.vue'),
  children: [
    {
      path: '/',
      name: 'newsletterRoot',
      props: true,
      component: () => import(/* webpackChunkName: "Newsletter" */ '../views/newsletter/unsubscribe/index.vue')
    },
    {
      path: 'unsubscribe',
      name: 'newsletterUnsubscribe',
      props: true,
      component: () => import(/* webpackChunkName: "Newsletter" */ '../views/newsletter/unsubscribe/index.vue')
    },
    {
      path: 'verify',
      name: 'newsletterVerify',
      props: true,
      component: () => import(/* webpackChunkName: "Newsletter" */ '../views/newsletter/verify/index.vue')
    },
  ]
}

const onboardingRoutes = {
  path: '/onboarding/:from?',
  name: 'onbgetstarted',
  component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/onboarding/index.vue'),
  beforeEnter(to, from, next) {
    store.dispatch('auth/init').then((state) => {
      if (state.authorized) {
        console.log("onboardingRoutes beforeEnter state authorized", to.fullPath)
        next()
      } else {
        console.log("onboardingRoutes beforeEnter state not authorized / ")
        next('/')
      }
    }).catch((e) => {
      console.log("onboardingRoutes beforeEnter catch /", e)
      next('/')
    })
  },
  children: [
    {
      path: 'fasttrack',
      name: 'fasttrack',
      props: true,
      component: () => import(/* webpackChunkName: "FinStrategyOnboarding" */ '../views/onboarding/fasttrack')
    },
  ]
}

const allowSignUp = process.env.VUE_APP_ALLOW_SIGN_UP === 'true'
if (allowSignUp) {
  routes.push(registrationRoutes)
}

routes.push(newsletterRoutes)
routes.push(onboardingRoutes)

// must be set as last route object for the 404 page to work
routes.push(publicRoutes)


const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    let target = 0

    if (to.hash) {
      return goTo(to.hash)
    } else if (savedPosition) {
      target = savedPosition.y
    }

    goTo(target)
  }
})

const checkVersion = async () => {
  try {
    console.log("checkVersion calling API finAPI.testVersion()")
    const testVersion = await finAPI.testVersion()
    const appVersion = process.env.VUE_APP_VERSION
    let result = compare(testVersion.compatibleVersion, appVersion, '<=')
    console.log("checkVersion", testVersion.compatibleVersion, appVersion, result)
    return result
  } catch (e) {
    console.log("error check version", e)
    return true
  }
}

// TODO check if we can optimize the routes handling by using beforeResolve instead of beforeEach
// see https://router.vuejs.org/guide/advanced/navigation-guards.html#global-resolve-guards
// see https://stackoverflow.com/questions/65200775/vue-router-beforeenter-vs-beforeeach

// TODO check if we should use router.onError()

// beforeEach triggers on every navigation
// beforeResolve also triggers on every navigation, but resolve guards are called right before the navigation is confirmed,
// after all in-component guards and async route components are resolved. router.beforeResolve is the ideal spot to fetch data or
// do any other operation that you want to avoid doing if the user cannot enter a page.
router.beforeEach(async (to, from, next) => {
  if (finBrowser.IS_IOS_OR_ANDROID_APP && to.path !== '/updateapp' && !await checkVersion()) {
    next('updateapp')
  }

  console.log("beforeEach", to.fullPath)

  store.dispatch('auth/init').then((state) => {
    doNavigationControl(state, from, to, next)
  }).catch((e) => {
    console.log("beforeEach error", from, to, e)
    next()
  })
})

export default router
